﻿import {
  Activity,
  LayoutDashboard,
  Network,
  Receipt,
  ScrollText,
  Server,
  Settings2,
  ShieldCheck,
  Tags,
  UserCircle,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { paths } from '@/router/paths';

import type { TranslationKey } from '@/i18n';

export interface NavLeaf {
  /** Schluessel im Woerterbuch - die Beschriftung folgt der Sprachwahl. */
  labelKey: TranslationKey;
  to: string;
  /** Farbpunkt vor dem Eintrag in aufgeklappten Gruppen. */
  dot?: string;
}

export interface NavSingle {
  labelKey: TranslationKey;
  icon: LucideIcon;
  to: string;
}

export interface NavGroup {
  labelKey: TranslationKey;
  icon: LucideIcon;
  items: NavLeaf[];
  /** Nur fuer Administratoren sichtbar. */
  adminOnly?: boolean;
}

export type NavEntry = NavGroup | NavSingle;

export function isNavGroup(entry: NavEntry): entry is NavGroup {
  return 'items' in entry;
}

/** Hauptnavigation der Kundenansicht. */
export const navEntries: NavEntry[] = [
  { labelKey: 'nav.dashboard', icon: LayoutDashboard, to: paths.dashboard },
  { labelKey: 'nav.instances', icon: Network, to: paths.instances.list },
  { labelKey: 'nav.plans', icon: Tags, to: paths.plans },
  { labelKey: 'nav.billing', icon: Wallet, to: paths.billing },
  { labelKey: 'nav.invoices', icon: Receipt, to: paths.invoices },
  { labelKey: 'nav.account', icon: UserCircle, to: paths.account },
];

/** Zusaetzliche Gruppe fuer Administratoren. */
export const adminGroup: NavGroup = {
  labelKey: 'nav.admin',
  icon: ShieldCheck,
  adminOnly: true,
  items: [
    { labelKey: 'nav.adminServers', to: paths.admin.servers, dot: 'var(--acc)' },
    { labelKey: 'nav.adminIpam', to: paths.admin.ipam, dot: 'var(--acc-2)' },
    { labelKey: 'nav.adminInstances', to: paths.admin.instances, dot: 'var(--info)' },
    { labelKey: 'nav.adminUsers', to: paths.admin.users, dot: 'var(--ok)' },
    { labelKey: 'nav.adminPlans', to: paths.admin.plans, dot: 'var(--acc)' },
    { labelKey: 'nav.adminBilling', to: paths.admin.billing, dot: 'var(--acc-2)' },
    { labelKey: 'nav.adminMail', to: paths.admin.mail, dot: 'var(--info)' },
    { labelKey: 'nav.adminSettings', to: paths.admin.settings, dot: 'var(--warn)' },
    { labelKey: 'nav.adminAudit', to: paths.admin.audit, dot: 'var(--warn)' },
  ],
};

/** Icons fuer Seitenkoepfe. */
export const pageIcons = {
  dashboard: LayoutDashboard,
  instances: Network,
  plans: Tags,
  account: UserCircle,
  servers: Server,
  users: Users,
  audit: ScrollText,
  settings: Settings2,
  activity: Activity,
} as const;
